/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { Badge, Checkbox, Tooltip } from '@/components/ui'

import { cappedTooltip, isGrantCapped } from './capRules'
import { APP_GRANTS, APP_GRANT_LABELS, type AppGrant, type PermissionsCap } from './types'
import type { UsageCount, UsageIndex } from './usage'

interface Props {
  grants: AppGrant[]
  onChange: (grants: AppGrant[]) => void
  cap: PermissionsCap | null
  usage: UsageIndex
  inUseLabel: (count: UsageCount) => string
  disabled?: boolean
}

/** One checkbox per app; an app the company has off is disabled with a tooltip saying so. */
export const AppGrantsSection: React.FC<Props> = ({ grants, onChange, cap, usage, inUseLabel, disabled }) => {
  const toggle = (grant: AppGrant, checked: boolean) =>
    onChange(checked ? [...grants.filter((g) => g !== grant), grant] : grants.filter((g) => g !== grant))

  return (
    <fieldset className="mt-3" data-testid="app-grants">
      <legend className="mb-1 text-sm font-medium">Apps</legend>
      <div className="flex flex-col gap-2">
        {APP_GRANTS.map((grant) => {
          const capped = isGrantCapped(cap, grant)
          const used = usage.forSlug(grant)
          const checkbox = (
            <Checkbox
              id={`grant-${grant}`}
              data-testid={`grant-${grant}`}
              label={APP_GRANT_LABELS[grant]}
              checked={grants.includes(grant)}
              disabled={disabled || capped}
              onChange={(checked) => toggle(grant, checked)}
              trailing={
                used.users > 0 ? (
                  <Badge variant="info" size="xs">
                    {inUseLabel(used)}
                  </Badge>
                ) : undefined
              }
            />
          )
          return capped && cap ? (
            <Tooltip key={grant} tip={cappedTooltip(APP_GRANT_LABELS[grant], cap.companyName)} position="right">
              {checkbox}
            </Tooltip>
          ) : (
            <React.Fragment key={grant}>{checkbox}</React.Fragment>
          )
        })}
      </div>
    </fieldset>
  )
}

export default AppGrantsSection
